import React, { useContext, useEffect, useState } from "react";
import AdminPageTitle from "./admin-page-title.tsx";
import { CommentData } from "../../entry/server/movie.ts";
import { videoRepo } from "../../repo/video-repo.ts";
import { AppContext } from "../../context/app-context.tsx";
import { Pagination } from "../../lib/api.ts";
import PaginationView from "../../componrnts/pagination.tsx";

export default function CommentsScreen() {
  const { setMessage, user } = useContext(AppContext);
  const [comments, setComments] = useState<CommentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);

  const [replyTargetId, setReplyTargetId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isReplySubmitting, setIsReplySubmitting] = useState(false);
  const [reviewSelections, setReviewSelections] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const fetchComments = async () => {
      setLoading(true);
      try {
        const response = await videoRepo.getComments(page);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت نظرات ناموفق بود." });
          setComments([]);
          return;
        }
        setComments(response.data);
        setPagination(response.pagination || null);
        setReviewSelections(response.data.reduce((acc, item) => {
          acc[item.id] = item.is_review;
          return acc;
        }, {} as Record<number, boolean>));
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [page, setMessage]);

  const onApprove = async (commentId: number) => {
    const isReview = reviewSelections[commentId] ?? false;
    try {
      const response = await videoRepo.approveComment(commentId, isReview ? "true" : "false");
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "تایید نظر با خطا مواجه شد." });
        return;
      }
      setComments((prev) =>
        prev.map((item) =>
          item.id === commentId ? { ...item, is_approved: true, is_review: isReview } : item
        )
      );
      setReviewSelections((prev) => {
        const next = { ...prev };
        delete next[commentId];
        return next;
      });
      setMessage({ type: "success", message: "نظر با موفقیت تایید شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  const onDelete = async (commentId: number) => {
    if (!window.confirm("آیا از حذف این نظر مطمئن هستید؟")) {
      return;
    }
    try {
      const response = await videoRepo.deleteComment(commentId);
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "حذف نظر با خطا مواجه شد." });
        return;
      }
      setComments((prev) => prev.filter((item) => item.id !== commentId && item.parent_id !== commentId));
      setMessage({ type: "success", message: "نظر با موفقیت حذف شد." });
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    }
  };

  const onStartReply = (commentId: number) => {
    setReplyTargetId(commentId);
    setReplyText("");
  };

  const onCancelReply = () => {
    setReplyTargetId(null);
    setReplyText("");
  };

  const onSubmitReply = async (comment: CommentData) => {
    if (!replyText.trim()) {
      setMessage({ type: "error", message: "متن پاسخ نمی‌تواند خالی باشد." });
      return;
    }

    setIsReplySubmitting(true);
    try {
      const response = await videoRepo.answerComment(comment.id, replyText.trim());
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ثبت پاسخ با خطا مواجه شد." });
        return;
      }

      const replyComment: CommentData = {
        id: (response.data as CommentData | null)?.id || Date.now(),
        user_id: 0,
        username: user?.username || "admin",
        movie_id: comment.movie_id,
        movie_title: comment.movie_title,
        comment: replyText.trim(),
        parent_id: comment.id,
        parent_username: comment.username,
        is_approved: true,
        is_spoiler: false,
        is_review: false,
        score: 0,
      };

      setComments((prev) => {
        const targetIndex = prev.findIndex((item) => item.id === comment.id);
        if (targetIndex === -1) {
          return [replyComment, ...prev];
        }
        const updated = [...prev];
        updated.splice(targetIndex + 1, 0, replyComment);
        return updated;
      });

      setMessage({ type: "success", message: "پاسخ با موفقیت ثبت شد." });
      onCancelReply();
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsReplySubmitting(false);
    }
  };

  return (
    <section className="space-y-4">
      <AdminPageTitle title="نظرات" />

      {loading ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          در حال دریافت نظرات...
        </div>
      ) : !comments.length ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">
          نظری برای نمایش وجود ندارد.
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => (
            <article
              key={comment.id}
              className={`rounded-2xl border p-4 ${
                comment.parent_id
                  ? "mr-5 border-indigo-500/30 bg-indigo-500/5"
                  : "border-slate-800 bg-slate-900/60"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-slate-100">{comment.username}</p>
                  <p className="text-xs text-slate-400">فیلم: {comment.movie_title}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs ${
                      comment.is_approved
                        ? "bg-emerald-500/20 text-emerald-300"
                        : "bg-amber-500/20 text-amber-300"
                    }`}
                  >
                    {comment.is_approved ? "تایید شده" : "در انتظار تایید"}
                  </span>
                  {comment.is_review && (
                    <span className="rounded-full bg-violet-500/20 px-2.5 py-1 text-xs text-violet-300">
                      نقد و بررسی
                    </span>
                  )}
                  {comment.parent_id > 0 && (
                    <span className="rounded-full bg-indigo-500/20 px-2.5 py-1 text-xs text-indigo-300">
                      پاسخ مدیر
                    </span>
                  )}
                </div>
              </div>

              <p className="mt-3 whitespace-pre-wrap text-sm text-slate-200">{comment.comment}</p>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {comment.parent_id === 0 && (
                  <>
                    {!comment.is_approved && (
                      <label className="flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-200">
                        <input
                          type="checkbox"
                          checked={reviewSelections[comment.id] ?? false}
                          onChange={(event) =>
                            setReviewSelections((prev) => ({
                              ...prev,
                              [comment.id]: event.target.checked,
                            }))
                          }
                          className="h-3.5 w-3.5 accent-indigo-500"
                        />
                        نقد و بررسی است
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => onApprove(comment.id)}
                      disabled={comment.is_approved}
                      className="rounded-lg border border-emerald-700 px-3 py-1.5 text-xs text-emerald-200 transition hover:border-emerald-500 hover:text-emerald-300 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      تایید
                    </button>
                    <button
                      type="button"
                      onClick={() => onStartReply(comment.id)}
                      className="rounded-lg border border-indigo-700 px-3 py-1.5 text-xs text-indigo-200 transition hover:border-indigo-500 hover:text-indigo-300"
                    >
                      پاسخ
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => onDelete(comment.id)}
                  className="rounded-lg border border-rose-700 px-3 py-1.5 text-xs text-rose-200 transition hover:border-rose-500 hover:text-rose-300"
                >
                  حذف
                </button>
              </div>

              {replyTargetId === comment.id && (
                <div className="mt-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-3">
                  <p className="mb-2 text-xs text-indigo-200">پاسخ به {comment.username}</p>
                  <textarea
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    rows={4}
                    placeholder="پاسخ مدیر را اینجا بنویسید..."
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none transition focus:border-indigo-400"
                  />
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onSubmitReply(comment)}
                      disabled={isReplySubmitting}
                      className="rounded-lg bg-indigo-500 px-4 py-2 text-xs font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isReplySubmitting ? "در حال ثبت..." : "ثبت پاسخ"}
                    </button>
                    <button
                      type="button"
                      onClick={onCancelReply}
                      className="rounded-lg border border-slate-700 px-4 py-2 text-xs text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
                    >
                      انصراف
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {pagination && <PaginationView pagination={pagination} onPageChange={setPage} />}
    </section>
  );
}
